import type { AxiosRequestConfig } from "axios";
import hashSum from "hash-sum";

/**
 * AxiosCanceler 类：用于管理重复请求
 * 使用 AbortController 替代已废弃的 CancelToken
 * 
 * 设计说明：
 * - addPending: 添加请求，如果有重复则取消前一个
 * - removePending: 仅清理记录，不取消请求（请求已完成/失败）
 * - clearPending: 主动取消所有进行中的请求（如路由切换）
 */
class AxiosCanceler {
    private pendingMap = new Map<string, AbortController>();

    /**
     * 生成请求 key（基于请求内容判断是否重复）
     */
    private getRequestKey(config: AxiosRequestConfig): string {
        const { method, url, params, data } = config;
        return hashSum({ method, url, params, data });
    }

    /**
     * 添加请求到待取消列表
     * 如果已存在相同请求，则取消前一个
     */
    public addPending(config: AxiosRequestConfig): AbortController {
        const requestKey = this.getRequestKey(config);

        // 如果已存在相同请求，则取消前一个请求
        if (this.pendingMap.has(requestKey)) {
            const controller = this.pendingMap.get(requestKey);
            controller?.abort();
            this.pendingMap.delete(requestKey);
        }

        // 创建新的 AbortController
        const controller = new AbortController();
        config.signal = controller.signal;
        this.pendingMap.set(requestKey, controller);

        return controller;
    }

    /**
     * 移除请求记录（仅清理，不取消）
     * 请求完成/失败后调用，只是清理记录
     */
    public removePending(config: AxiosRequestConfig): void {
        const requestKey = this.getRequestKey(config);
        this.pendingMap.delete(requestKey);
    }

    /**
     * 主动取消所有进行中的请求
     * 用于路由切换、用户登出等场景
     */
    public clearPending(): void {
        this.pendingMap.forEach((controller) => {
            controller.abort();
        });
        this.pendingMap.clear();
    }
}

export const axiosCanceler = new AxiosCanceler();